import './ArtistsPage.scss'

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';
import Avatar from '../../components/Avatar/Avatar';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faFilter } from "@fortawesome/free-solid-svg-icons";
import { useCurrentUser } from '../../UserProvider';
import LocationInput from '../../components/Form/LocationInput';
import { GENRES, INSTRUMENTS } from "../../constants";
import classNames from 'classnames';
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import usePageTitle from "../../hooks/PageTitle";

export default function ArtistsPage() {
  usePageTitle("Artists");

  const { currentUser } = useCurrentUser();

  const [profiles, setProfiles]             = useState([]);
  const [page, setPage]                     = useState(1);
  const [hasMore, setHasMore]               = useState(true);
  const [loading, setLoading]               = useState(true);
  const [displayFilters, setDisplayFilters] = useState(false);
  const perPage = 10;

  const [filters, setFilters] = useState({
    query:       "",
    location:    "",
    latitude:    null,
    longitude:   null,
    distance:    25,
    genres:      [],
    instruments: [],
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const toggleGenre = (genre) => {
    setFilters(f => {
      const newGenres = f.genres.includes(genre) ? f.genres.filter(g => g !== genre) : [...f.genres, genre];
      return { ...f, genres: newGenres };
    });
  };

  const toggleInstrument = (instrument) => {
    setFilters(f => {
      const newInst = f.instruments.includes(instrument) ? f.instruments.filter(i => i !== instrument) : [...f.instruments, instrument];
      return { ...f, instruments: newInst };
    });
  };

  const handleDistanceChange = (value) => {
    setFilters(f => ({ ...f, distance: value }));
  };

  const clearLocationValues = () => {
    setFilters(f => ({ ...f, location: "", latitude: null, longitude: null }));
  };

  const handleLocationInputClear = () => {
    clearLocationValues();
  };

  const handleLocationPlaceChange = (place) => {
    if (!place || !place.address || !place.latitude || !place.longitude) {
      return clearLocationValues();
    }
    setFilters(f => ({
      ...f,
      location:  place.address,
      latitude:  place.latitude,
      longitude: place.longitude,
    }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      if (filters !== debouncedFilters) {
        setProfiles([]);
        setHasMore(true);
        setLoading(true);
      }
      setDebouncedFilters(filters);
    }, 800);
    return () => clearTimeout(handler);
  }, [filters, debouncedFilters]);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const { data } = await api.post(`/profiles/search`, {
          query:       debouncedFilters.query,
          page:        page,
          per_page:    perPage,
          genres:      debouncedFilters.genres.length > 0 ? debouncedFilters.genres : null,
          instruments: debouncedFilters.instruments.length > 0 ? debouncedFilters.instruments : null,
          latitude:    debouncedFilters.latitude,
          longitude:   debouncedFilters.longitude,
          distance:    debouncedFilters.distance,
        });
        setLoading(false);
        if (page === 1) {
          setProfiles(data);
        } else {
          setProfiles(prev => [...prev, ...data]);
        }
        if (data.length < perPage) {
          setHasMore(false);
        }
      } catch (err) {
        console.error('Error fetching profiles:', err);
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [debouncedFilters, page]);

  const reIndex = async () => {
    await api.post(`/profiles/embed`);
    alert('Done indexing profiles');
  };

  return (
    <div className='__artists'>
      <div className='content'>
        {currentUser?.is_admin &&
          <div className='admin-buttons'>
            <button className='ppy-btn ppy-btn-primary' onClick={reIndex}>
              Index Profiles
            </button>
          </div>
        }

        <div className='main'>
          {displayFilters &&
            <div className='filters glass'>
              <div className='filters-header'>
                Filters
                <button className='btn-close' onClick={() => setDisplayFilters(false)} />
              </div>

              <label>Address</label>
              <LocationInput
                className="location"
                name="location"
                placeholder="Enter location"
                value={filters.location}
                onInputClear={handleLocationInputClear}
                onPlaceChange={handleLocationPlaceChange}
              />

              <label>Range</label>
              <Slider
                min={5}
                max={100}
                value={filters.distance}
                onChange={handleDistanceChange}
                disabled={!filters.location.trim()}
              />
              <div className='mileage'><label>{filters.distance} miles</label></div>

              <label>Instruments</label>
              <div className="genre-grid">
                {INSTRUMENTS.map((instrument) => (
                  <div
                    key={instrument}
                    className={classNames("genre", { "selected": filters.instruments.includes(instrument) })}
                    onClick={() => toggleInstrument(instrument)}
                  >
                    {instrument}
                  </div>
                ))}
              </div>

              <label>Genre</label>
              <div className="genre-grid">
                {GENRES.map((genre) => (
                  <div
                    key={genre}
                    className={classNames("genre", { "selected": filters.genres.includes(genre) })}
                    onClick={() => toggleGenre(genre)}
                  >
                    {genre}
                  </div>
                ))}
              </div>

              <button className='mobile-filter ppy-btn ppy-btn-primary' onClick={() => setDisplayFilters(false)}>
                Filter <FontAwesomeIcon icon={faFilter} />
              </button>
            </div>
          }

          <div className='results'>
            <div className='controls'>
              <button className='filter-toggle' onClick={() => setDisplayFilters(!displayFilters)}>
                <FontAwesomeIcon icon={faFilter} />
              </button>
              <input
                className='search'
                type="text"
                placeholder="Search artists..."
                value={filters.query}
                onChange={(e) => setFilters(f => ({ ...f, query: e.target.value }))}
              />
            </div>

            {!loading && profiles.length === 0 && (
              <div className='empty-state'>
                <div className='no-results'>No results found</div>
                <img src="/images/guy.svg" alt="Guy" />
              </div>
            )}

            <div className='profiles'>
              {[0, 1].map((col) => (
                <div key={col} className='column'>
                  {profiles
                    .filter((_, i) => i % 2 === col)
                    .map((profile) => (
                      <div key={profile.id} className='profile-item'>
                        <Link to={`/a/${profile.display_name}`}>
                          <div className='profile-card'>
                            <Avatar profile={profile} className='avatar' />
                            {(profile.name || profile.bio || profile.genres?.length > 0 || profile.address) && (
                              <div className='profile-card__overlay'>
                                {profile.name && (
                                  <div className='profile-name'>{profile.name}</div>
                                )}
                                {profile.bio && (
                                  <div className='profile-bio'>{profile.bio}</div>
                                )}
                                {profile.genres?.length > 0 && (
                                  <ul className='genre-pills'>
                                    {profile.genres.map((genre, i) => (
                                      <li key={i} className='genre-pill'>{genre}</li>
                                    ))}
                                  </ul>
                                )}
                                {profile.address && (
                                  <div className='profile-location'>
                                    <FontAwesomeIcon icon={faLocationDot} /> {profile.address}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </Link>
                      </div>
                    ))}
                </div>
              ))}
            </div>

            {hasMore && profiles.length > 0 && (
              <div className="load-more">
                <button
                  className="ppy-btn ppy-btn-primary"
                  onClick={() => setPage(prev => prev + 1)}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
